import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom-v6'
import { Tabs, TabsList, TabsTrigger } from '@clouway/ui'

import { useTrackingObjectConfig, useUpdateTrackingObjectConfig } from '../hooks'
import type { TrackingObjectConfig } from '../types'

const EMPTY_CONFIG: TrackingObjectConfig = {
  id: '',
  analogPortConfigs: [],
  digitalPortConfigs: [],
  sensorConfiguration: { temperature: [], magnetStatus: [], batteryIndication: [], batteryVoltage: [] },
}

export interface ConfigOutletContext {
  config: TrackingObjectConfig
  // Persists the whole document; resolves when the PUT completes.
  save: (next: TrackingObjectConfig) => Promise<unknown>
  saving: boolean
  loading: boolean
}

export function useConfigContext() {
  return useOutletContext<ConfigOutletContext>()
}

const TABS = ['analogs', 'digitals', 'sensors'] as const

export function ConfigLayout() {
  const { t } = useTranslation('common')
  const { id = '', tid = '' } = useParams<{ id: string; tid: string }>()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { data, isLoading } = useTrackingObjectConfig(id, tid)
  const update = useUpdateTrackingObjectConfig(id, tid)

  const active = TABS.find((tab) => pathname.endsWith(`/${tab}`))
  // Always render the Outlet so the index redirect (→ analogs) fires regardless
  // of the config query; tabs gate their own content on `loading`.
  const context: ConfigOutletContext = {
    config: data ?? EMPTY_CONFIG,
    save: (next) => update.mutateAsync(next),
    saving: update.isLoading,
    loading: isLoading,
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
      {/* Replace (not push) so cycling tabs doesn't stack history entries —
          the back button then returns to the page before config, not the
          previously-viewed tab. Matches the index redirect's `replace`. */}
      <Tabs value={active} onValueChange={(value) => navigate(`../config/${value}`, { replace: true })}>
        <TabsList>
          <TabsTrigger value="analogs">{t('trackingObjects.configs.analogs')}</TabsTrigger>
          <TabsTrigger value="digitals">{t('trackingObjects.configs.digitals')}</TabsTrigger>
          <TabsTrigger value="sensors">{t('trackingObjects.configs.sensors')}</TabsTrigger>
        </TabsList>
      </Tabs>

      <Outlet context={context} />
    </div>
  )
}
